import React, { useState, useEffect, useCallback } from 'react';
import {
  FiSearch,
  FiMoreVertical,
  FiUserCheck,
  FiUserX,
  FiTrash2,
  FiUsers,
  FiCheckCircle,
  FiXCircle,
  FiAlertCircle,
  FiTrendingUp
} from 'react-icons/fi';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { useNotification } from '../../contexts/NotificationContext';
import { adminAPI } from '../../services/api';
import { useNavigate } from 'react-router-dom';

const StudentsManagement = () => {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [gradeFilter, setGradeFilter] = useState('all');
  const [menuOpen, setMenuOpen] = useState(null);
  const [stats, setStats] = useState({ total: 0, active: 0, inactive: 0, suspended: 0 });
  const [rechargeOpen, setRechargeOpen] = useState(false);
  const [rechargePhone, setRechargePhone] = useState('');
  const [rechargeAmount, setRechargeAmount] = useState('');
  const [rechargeNetwork, setRechargeNetwork] = useState('');
  const [rechargeLoading, setRechargeLoading] = useState(false);
  const [rechargeUserId, setRechargeUserId] = useState(null);
  const { showError, showSuccess } = useNotification();
  const navigate = useNavigate();

  const fetchStudents = useCallback(async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getAllUsers({ role: 'student' });
      const raw = response?.data?.data || [];
      const normalized = raw.map((u) => {
        let resolvedGrade = u.grade || u.schoolGrade || u.class || (u.syllabus && u.syllabus.class);
        if (!resolvedGrade) {
          const examTypeArr = Array.isArray(u.examType) ? u.examType : (typeof u.examType === 'string' && u.examType ? [u.examType] : []);
          resolvedGrade = examTypeArr.length ? examTypeArr.join(', ') : null;
        }
        return {
          id: u._id || u.id,
          _id: u._id || u.id,
          name: u.fullName || `${u.firstName || ''} ${u.lastName || ''}`.trim(),
          email: u.email || '',
          avatar: u.avatar || '',
          phone: u.phoneNumber || '',
          grade: resolvedGrade,
          status: u.status || (u.isActive === false ? 'inactive' : 'active'),
          progress: u.progress || 0,
          testsCount: (u.performance && u.performance.totalTests) || u.testsCount || 0,
          averageScore: (u.performance && u.performance.averageScore) || u.averageScore || null,
          createdAt: u.createdAt,
          airtimeRechargeStatus: u.airtimeRechargeStatus || 'not_recharged'
        };
      });
      setStudents(normalized);
    } catch (error) {
      showError('Error fetching students');
    } finally {
      setLoading(false);
    }
  }, [showError]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await adminAPI.getUsersStats({ role: 'student' });
      const data = response?.data?.data || {};
      setStats({ total: data.total || 0, active: data.active || 0, inactive: data.inactive || 0, suspended: data.suspended || 0 });
    } catch (_) {}
  }, []);

  useEffect(() => {
    fetchStudents();
    fetchStats();
  }, [fetchStudents, fetchStats]);

  const handleStatusChange = async (studentId, newStatus) => {
    try {
      await adminAPI.updateUserStatus(studentId, newStatus);
      showSuccess('Student status updated');
      fetchStudents();
      fetchStats();
      setMenuOpen(null);
    } catch (error) {
      showError('Failed to update student status');
    }
  };

  const handleDeleteStudent = async (studentId) => {
    if (!window.confirm('Are you sure you want to delete this student?')) return;
    try {
      await adminAPI.deleteUser(studentId);
      showSuccess('Student deleted');
      fetchStudents();
      fetchStats();
      setMenuOpen(null);
    } catch (error) {
      showError('Failed to delete student');
    }
  };

  const handleViewProgress = (studentId) => {
    setMenuOpen(null);
    navigate(`/students/progress/${studentId}`);
  };

  const openRecharge = (student) => {
    setRechargeUserId(student._id || student.id);
    setRechargePhone(student.phone || '');
    setRechargeAmount('100');
    setRechargeNetwork('');
    setRechargeOpen(true);
  };

  const submitRecharge = async () => {
    try {
      if (!rechargePhone || !rechargeAmount) return;
      setRechargeLoading(true);
      const amt = parseInt(rechargeAmount, 10);
      await adminAPI.rechargePhone({ phone: rechargePhone, amount: amt, network: rechargeNetwork || undefined, userId: rechargeUserId });
      showSuccess('Recharge initiated');
      setRechargeOpen(false);
      await fetchStudents();
    } catch (error) {
      showError('Failed to recharge');
    } finally {
      setRechargeLoading(false);
    }
  };

  const filteredStudents = students.filter((s) => {
    const matchSearch = (s.name || '').toLowerCase().includes(searchTerm.toLowerCase()) || (s.email || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchStatus = statusFilter === 'all' || s.status === statusFilter;
    const matchGrade = gradeFilter === 'all' || String(s.grade) === gradeFilter;
    return matchSearch && matchStatus && matchGrade;
  });

  const statusBadge = (status) => {
    const map = { active: 'bg-emerald-100 text-emerald-800', inactive: 'bg-slate-100 text-slate-700', suspended: 'bg-red-100 text-red-800' };
    return <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${map[status] || 'bg-slate-100 text-slate-700'}`}>{status}</span>;
  };

  const progressBar = (progress) => {
    const p = Number(progress) || 0;
    const color = p >= 80 ? 'bg-emerald-500' : p >= 60 ? 'bg-amber-500' : 'bg-red-400';
    return (
      <div className="flex items-center gap-2 min-w-[100px]">
        <div className="flex-1 h-2 rounded-full bg-slate-200 overflow-hidden">
          <div className={`h-full rounded-full ${color}`} style={{ width: `${Math.min(100, p)}%` }} />
        </div>
        <span className="text-xs text-slate-600 tabular-nums">{p}%</span>
      </div>
    );
  };

  const rechargeStatusBadge = (status) => {
    const map = { recharged: 'bg-emerald-100 text-emerald-800', failed: 'bg-red-100 text-red-800', not_recharged: 'bg-slate-100 text-slate-600' };
    return <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${map[status] || 'bg-slate-100 text-slate-600'}`}>{status}</span>;
  };

  const grades = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'];

  if (loading && students.length === 0) {
    return (
      <div className="min-h-screen bg-slate-50/60 flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50/60">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-xl font-semibold text-slate-900 tracking-tight">Students</h1>
            <p className="text-sm text-slate-500 mt-0.5">Manage student accounts, progress, and airtime recharge.</p>
          </div>
          <button type="button" onClick={() => navigate('/students/new')} className="inline-flex items-center gap-2 px-4 py-2.5 bg-slate-900 text-white text-sm font-medium rounded-lg hover:bg-slate-800 transition-colors shadow-sm">
            Add Student
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-6 py-3 px-4 rounded-lg bg-white border border-slate-200/80 shadow-sm mb-6">
          <div className="flex items-center gap-2">
            <FiUsers className="h-4 w-4 text-slate-400" />
            <span className="text-sm text-slate-600">Total</span>
            <span className="text-sm font-semibold text-slate-900">{stats.total}</span>
          </div>
          <div className="h-4 w-px bg-slate-200" />
          <div className="flex items-center gap-2">
            <FiCheckCircle className="h-4 w-4 text-emerald-500" />
            <span className="text-sm text-slate-600">Active</span>
            <span className="text-sm font-semibold text-slate-900">{stats.active}</span>
          </div>
          <div className="h-4 w-px bg-slate-200" />
          <div className="flex items-center gap-2">
            <FiXCircle className="h-4 w-4 text-slate-400" />
            <span className="text-sm text-slate-600">Inactive</span>
            <span className="text-sm font-semibold text-slate-900">{stats.inactive}</span>
          </div>
          <div className="h-4 w-px bg-slate-200" />
          <div className="flex items-center gap-2">
            <FiAlertCircle className="h-4 w-4 text-red-500" />
            <span className="text-sm text-slate-600">Suspended</span>
            <span className="text-sm font-semibold text-slate-900">{stats.suspended}</span>
          </div>
        </div>

        <div className="mb-6 flex flex-col sm:flex-row gap-3 sm:items-center flex-wrap">
          <div className="relative flex-1 min-w-0 max-w-sm">
            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Search students..." className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-slate-400/30 focus:border-slate-400 placeholder:text-slate-400" />
          </div>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-slate-400/30 focus:border-slate-400">
            <option value="all">All status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="suspended">Suspended</option>
          </select>
          <select value={gradeFilter} onChange={(e) => setGradeFilter(e.target.value)} className="px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-slate-400/30 focus:border-slate-400">
            <option value="all">All grades</option>
            {grades.map((g) => <option key={g} value={g}>Grade {g}</option>)}
          </select>
          {filteredStudents.length > 0 && <span className="text-xs text-slate-500 sm:ml-auto">{filteredStudents.length} of {students.length}</span>}
        </div>

        <div className="rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden">
          {loading ? (
            <div className="flex justify-center py-16"><LoadingSpinner /></div>
          ) : filteredStudents.length === 0 ? (
            <div className="text-center py-14 px-6">
              <FiUsers className="h-10 w-10 text-slate-300 mx-auto mb-3" />
              <p className="text-sm text-slate-500">No students match your criteria.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50/80">
                    <th className="px-4 py-3 text-left font-medium text-slate-700">Student</th>
                    <th className="px-4 py-3 text-left font-medium text-slate-700">Email</th>
                    <th className="px-4 py-3 text-left font-medium text-slate-700">Grade</th>
                    <th className="px-4 py-3 text-left font-medium text-slate-700">Status</th>
                    <th className="px-4 py-3 text-left font-medium text-slate-700">Progress</th>
                    <th className="px-4 py-3 text-left font-medium text-slate-700">Tests</th>
                    <th className="px-4 py-3 text-left font-medium text-slate-700">Avg score</th>
                    <th className="px-4 py-3 text-left font-medium text-slate-700">Recharge</th>
                    <th className="px-4 py-3 text-left font-medium text-slate-700">Joined</th>
                    <th className="px-4 py-3 w-0" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredStudents.map((student) => (
                    <tr key={student.id} className="hover:bg-slate-50/50 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="h-9 w-9 rounded-full bg-slate-200 flex items-center justify-center text-sm font-medium text-slate-700 shrink-0 overflow-hidden">
                            {student.avatar ? <img src={student.avatar} alt="" className="h-full w-full object-cover" /> : (student.name && student.name.charAt(0).toUpperCase())}
                          </div>
                          <div>
                            <div className="font-medium text-slate-900">{student.name}</div>
                            <div className="text-xs text-slate-500">ID: {student.id}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-slate-600">{student.email}</td>
                      <td className="px-4 py-3 text-slate-600">{student.grade ? `Grade ${student.grade}` : '—'}</td>
                      <td className="px-4 py-3">{statusBadge(student.status)}</td>
                      <td className="px-4 py-3">{progressBar(student.progress)}</td>
                      <td className="px-4 py-3 text-slate-600">{student.testsCount ?? 0}</td>
                      <td className="px-4 py-3 text-slate-600">{student.averageScore != null ? `${Number(student.averageScore).toFixed(1)}%` : 'N/A'}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {rechargeStatusBadge(student.airtimeRechargeStatus)}
                          <button type="button" onClick={() => openRecharge(student)} className="text-xs px-2 py-1 border border-slate-200 rounded bg-white text-slate-700 hover:bg-slate-50">Recharge</button>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-slate-600">{student.createdAt ? new Date(student.createdAt).toLocaleDateString() : '—'}</td>
                      <td className="px-4 py-3 text-right">
                        <div className="relative inline-block">
                          <button type="button" onClick={() => setMenuOpen(menuOpen === student.id ? null : student.id)} className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-700 transition-colors"><FiMoreVertical className="h-4 w-4" /></button>
                          {menuOpen === student.id && (
                            <div className="absolute right-0 top-full mt-1 py-1 w-40 rounded-lg border border-slate-200 bg-white shadow-lg z-10">
                              <button type="button" onClick={() => handleViewProgress(student._id)} className="w-full px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2"><FiTrendingUp className="h-4 w-4" /> View Progress</button>
                              <button type="button" onClick={() => handleStatusChange(student._id, student.status === 'active' ? 'inactive' : 'active')} className="w-full px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2">{student.status === 'active' ? <FiUserX className="h-4 w-4" /> : <FiUserCheck className="h-4 w-4" />}{student.status === 'active' ? 'Deactivate' : 'Activate'}</button>
                              <button type="button" onClick={() => handleStatusChange(student._id, 'suspended')} disabled={student.status === 'suspended'} className="w-full px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50">Suspend</button>
                              <button type="button" onClick={() => handleDeleteStudent(student._id)} className="w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"><FiTrash2 className="h-4 w-4" /> Delete</button>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {rechargeOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={() => !rechargeLoading && setRechargeOpen(false)}>
          <div className="bg-white rounded-xl shadow-xl max-w-sm w-full p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-slate-900 mb-4">Recharge Airtime</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Phone</label>
                <input type="text" value={rechargePhone} onChange={(e) => setRechargePhone(e.target.value)} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400/30 focus:border-slate-400" placeholder="Phone number" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Amount (NGN)</label>
                <input type="number" value={rechargeAmount} onChange={(e) => setRechargeAmount(e.target.value)} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400/30 focus:border-slate-400" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Network</label>
                <select value={rechargeNetwork} onChange={(e) => setRechargeNetwork(e.target.value)} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-slate-400/30 focus:border-slate-400">
                  <option value="">Auto</option>
                  <option value="mtn">MTN</option>
                  <option value="airtel">Airtel</option>
                  <option value="glo">Glo</option>
                  <option value="9mobile">9mobile</option>
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button type="button" onClick={() => !rechargeLoading && setRechargeOpen(false)} className="px-4 py-2 text-sm font-medium text-slate-700 border border-slate-200 rounded-lg bg-white hover:bg-slate-50">Cancel</button>
              <button type="button" onClick={submitRecharge} disabled={rechargeLoading} className="px-4 py-2 text-sm font-medium text-white bg-slate-900 rounded-lg hover:bg-slate-800 disabled:opacity-60">{rechargeLoading ? 'Processing...' : 'Recharge'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentsManagement;
